/**
 * TemplateAnalyticsWidget - Sprint 11 v2
 * Template usage analytics widget with sparkline, department adoption, and CSV export
 * v2: Added Top 5 by conversion, department filter, date presets, help tooltips
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BookTemplate, Download, RefreshCw, TrendingUp, Clock,
  Users, Building2, ArrowUpRight, BarChart3, HelpCircle,
  Trophy, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format time saved
const formatTimeSaved = (seconds) => {
  if (!seconds) return '0 min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} min`;
};

// Time saved calculation tooltip
const TimeSavedTooltip = () => (
  <div className="relative group inline-block">
    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 w-64 pointer-events-none">
      <p className="font-medium mb-1">Time Saved Calculation</p>
      <p>Estimated 15 minutes saved per template use compared to manual demand entry.</p>
      <p className="mt-1 text-gray-300">Formula: Total Uses × 15 min</p>
    </div>
  </div>
);

const TemplateAnalyticsWidget = ({ expanded = false }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [departments, setDepartments] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/demand-templates/analytics/usage?range=${timeRange}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
        
        // Extract unique departments from data
        const depts = new Set();
        result.department_adoption?.forEach(d => depts.add(d.department));
        result.data?.forEach(t => t.departments?.forEach(d => depts.add(d)));
        setDepartments(Array.from(depts).filter(Boolean));
      } else {
        toast.error('Failed to load template analytics');
      }
    } catch (err) {
      console.error('Failed to load template analytics:', err);
      toast.error('Failed to load template analytics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, timeRange]);

  useEffect(() => {
    fetchData();
    
    // Update URL with filters
    const params = new URLSearchParams(window.location.search);
    params.set('range', timeRange.toString());
    if (departmentFilter) params.set('dept', departmentFilter);
    else params.delete('dept');
    
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [fetchData, timeRange, departmentFilter]);

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rangeParam = params.get('range');
    const deptParam = params.get('dept');
    
    if (rangeParam) setTimeRange(parseInt(rangeParam));
    if (deptParam) setDepartmentFilter(deptParam);
  }, []);

  const exportToCSV = () => {
    if (!data?.data?.length) {
      toast.error('No data to export');
      return;
    }

    // Apply department filter to export
    let exportData = data.data;
    if (departmentFilter) {
      exportData = exportData.filter(t => 
        t.departments?.includes(departmentFilter)
      );
    }

    const headers = ['Template Name', 'Total Uses', 'Unique Users', 'Departments', 'Converted', 'Conversion Rate', 'Time Saved', 'Last Used'];
    const rows = exportData.map(t => [
      t.template_name,
      t.total_uses,
      t.unique_users,
      t.department_count,
      t.converted_count,
      `${t.conversion_rate}%`,
      formatTimeSaved(t.time_saved_sec),
      t.last_used ? new Date(t.last_used).toLocaleDateString() : '-'
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `template_analytics_${timeRange}d${departmentFilter ? `_${departmentFilter}` : ''}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  if (loading && !data) {
    return (
      <Card data-testid="template-analytics-widget">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const trend = data?.trend || [];
  const deptAdoption = data?.department_adoption || [];
  let templates = data?.data || [];

  // Apply department filter
  if (departmentFilter) {
    templates = templates.filter(t => t.departments?.includes(departmentFilter));
  }

  // Top 5 by conversion rate
  const top5ByConversion = [...templates]
    .filter(t => t.total_uses >= 3) // Minimum uses for meaningful conversion
    .sort((a, b) => b.conversion_rate - a.conversion_rate)
    .slice(0, 5);

  return (
    <div className="space-y-6" data-testid="template-analytics-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BookTemplate className="h-5 w-5" />
            Template Usage Analytics
          </h2>
          <p className="text-sm text-gray-500">Track template adoption and efficiency gains</p>
        </div>
        <div className="flex gap-2">
          {/* Date Range Presets */}
          <div className="flex border rounded-md overflow-hidden" data-testid="date-presets">
            {[7, 30, 90, 365].map(days => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={`px-3 py-1.5 text-sm ${
                  timeRange === days 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                {days === 365 ? '1y' : `${days}d`}
              </button>
            ))}
          </div>
          
          {/* Department Filter */}
          {departments.length > 0 && (
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="px-3 py-1.5 border rounded text-sm"
              data-testid="department-filter"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          )}
          
          <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Total Uses</p>
                <p className="text-2xl font-bold">{summary.total_uses || 0}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                  Time Saved
                  <TimeSavedTooltip />
                </span>
                <p className="text-2xl font-bold">{summary.total_time_saved_hours || 0}h</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">Templates Active</p>
                <p className="text-2xl font-bold">{summary.total_templates_used || 0}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <BookTemplate className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium">Avg. Conversion</p>
                <p className="text-2xl font-bold">
                  {templates.length > 0 
                    ? Math.round(templates.reduce((sum, t) => sum + t.conversion_rate, 0) / templates.length)
                    : 0}%
                </p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg">
                <ArrowUpRight className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top 5 by Conversion Rate + Charts */}
      <div className="grid grid-cols-3 gap-6">
        {/* Top 5 by Conversion Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-500" />
              Top 5 by Conversion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top5ByConversion.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">
                Not enough data (min 3 uses required)
              </div>
            ) : (
              <div className="space-y-3">
                {top5ByConversion.map((t, idx) => (
                  <div key={t.template_id || idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-amber-600 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="text-sm truncate max-w-[120px]" title={t.template_name}>
                        {t.template_name}
                      </span>
                    </div>
                    <Badge className="bg-green-100 text-green-700">
                      {t.conversion_rate}%
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Trend Sparkline */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Usage Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40">
              {trend.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  No usage data in this period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }}
                      tickFormatter={(val) => val.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip labelFormatter={(label) => `Date: ${label}`} />
                    <Line 
                      type="monotone" 
                      dataKey="uses" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', r: 2 }}
                      name="Uses"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Department Adoption */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Department Adoption
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40">
              {deptAdoption.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  No department data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={deptAdoption} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis 
                      dataKey="department" 
                      type="category" 
                      width={80}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(val) => val?.slice(0, 10) + (val?.length > 10 ? '...' : '')}
                    />
                    <Tooltip />
                    <Bar dataKey="uses" fill="#8b5cf6" name="Uses" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates Table with Conversion Rates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Template Performance</CardTitle>
              <CardDescription>Conversion rate and efficiency metrics by template</CardDescription>
            </div>
            {departmentFilter && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Filter className="h-3 w-3" />
                {departmentFilter}
                <button 
                  onClick={() => setDepartmentFilter('')}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium">Template</th>
                  <th className="text-right py-2 font-medium">Uses</th>
                  <th className="text-right py-2 font-medium">Users</th>
                  <th className="text-right py-2 font-medium">Depts</th>
                  <th className="text-right py-2 font-medium">Converted</th>
                  <th className="text-right py-2 font-medium">Conv. Rate</th>
                  <th className="text-right py-2 font-medium">
                    <span className="flex items-center justify-end gap-1">
                      Time Saved
                      <TimeSavedTooltip />
                    </span>
                  </th>
                  <th className="text-right py-2 font-medium">Last Used</th>
                </tr>
              </thead>
              <tbody>
                {templates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-400">
                      {departmentFilter 
                        ? `No templates used by ${departmentFilter} in this period`
                        : 'No template usage in this period'
                      }
                    </td>
                  </tr>
                ) : (
                  templates.map((t, idx) => (
                    <tr key={t.template_id || idx} className="border-b hover:bg-gray-50">
                      <td className="py-2">
                        <span className="font-medium">{t.template_name}</span>
                      </td>
                      <td className="py-2 text-right">{t.total_uses}</td>
                      <td className="py-2 text-right">{t.unique_users}</td>
                      <td className="py-2 text-right">{t.department_count}</td>
                      <td className="py-2 text-right">{t.converted_count}</td>
                      <td className="py-2 text-right">
                        <Badge className={
                          t.conversion_rate >= 50 ? 'bg-green-100 text-green-700' :
                          t.conversion_rate >= 25 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {t.conversion_rate}%
                        </Badge>
                      </td>
                      <td className="py-2 text-right text-green-600">
                        {formatTimeSaved(t.time_saved_sec)}
                      </td>
                      <td className="py-2 text-right text-gray-500 text-xs">
                        {t.last_used ? new Date(t.last_used).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TemplateAnalyticsWidget;
